import { Grid, Typography } from '@mui/material';
import Button from 'components/Button';
import { AppTextInput } from 'components/FormElements';
import FullHeightView from 'components/new/FullHeightView';
import VehicleSelector from 'components/new/VehicleSelector';
import DatePicker from 'further-ui/components/DatePicker';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';
import useEarliestAvailableLedgerDate from 'hooks/lp/data/vehicle/useEarliestAvailableLedgerDate';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Action,
  CreateCapitalActionFormValues,
} from './hooks/useCapitalActionForm';

type Props = {
  lastAction: Action | null;
  onCancel: () => void;
  onSaveAndExit: () => void;
  onNext: () => void;
  vehicle: {
    id: string;
    vehicleName: string;
  };
  isLoading: boolean;
};

const CapitalActionDetails: React.FC<Props> = ({
  lastAction,
  onCancel,
  onSaveAndExit,
  onNext,
  vehicle,
  isLoading,
}) => {
  const { control } = useFormContext<CreateCapitalActionFormValues>();
  const earliestAvailableLedgerDate = useEarliestAvailableLedgerDate({
    vehicleId: vehicle.id,
    limitByCapitalActions: true,
  });

  return (
    <FullHeightView
      actionBar={
        <Row centered spacing="sm" justify="space-between">
          <Button
            size="large"
            variant="outlined"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Row centered spacing="sm" justify="flex-end">
            <Button
              size="large"
              variant="outlined"
              onClick={onSaveAndExit}
              loading={lastAction === 'saveAndExit' && isLoading}
              disabled={isLoading}
            >
              Save and exit
            </Button>
            <Button
              size="large"
              variant="contained"
              onClick={onNext}
              loading={lastAction === 'saveAndNext' && isLoading}
              disabled={isLoading}
            >
              Save and next
            </Button>
          </Row>
        </Row>
      }
    >
      <Grid container>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Column spacing="lg">
            <Typography variant="h4">Enter core capital action data</Typography>
            <FormField label="Vehicle" size="md">
              <VehicleSelector selectedVehicle={vehicle} />
            </FormField>
            <FormField label="Capital action name" size="md">
              <Controller
                name="name"
                control={control}
                render={({ fieldState: { error }, field }) => (
                  <AppTextInput
                    {...field}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </FormField>
            <FormField
              label="Capital action date"
              size="md"
              tooltip="This is the accounting date of this capital action."
            >
              <Controller
                name="ledgerDate"
                control={control}
                render={({
                  fieldState: { error },
                  field: { value, onChange, ...rest },
                }) => (
                  <DatePicker
                    fullWidth
                    error={!!error}
                    helperText={error?.message?.toString()}
                    value={value}
                    onChange={onChange}
                    minDate={earliestAvailableLedgerDate?.toUTCDate()}
                    {...rest}
                  />
                )}
              />
            </FormField>

            <FormField
              label="Capital action due date"
              size="md"
              tooltip="This is the payment due date that will be communicated to LPs if the action is a net capital call."
            >
              <Controller
                name="actionDueDate"
                control={control}
                render={({
                  fieldState: { error },
                  field: { value, onChange, ...rest },
                }) => (
                  <DatePicker
                    fullWidth
                    error={!!error}
                    helperText={error?.message?.toString()}
                    value={value}
                    onChange={onChange}
                    minDate={earliestAvailableLedgerDate?.toUTCDate()}
                    {...rest}
                  />
                )}
              />
            </FormField>
          </Column>
        </Grid>
      </Grid>
    </FullHeightView>
  );
};

export default CapitalActionDetails;
